import React, { useState, useRef, useEffect, ReactNode } from 'react';
import { ChevronDown, Check, X, Search } from 'lucide-react';

/** Single option value (string or number) */
export type SelectOptionValue = string | number;

/** Selected value: single value or array for multiple mode */
export type SelectValue = SelectOptionValue | SelectOptionValue[];

export interface SelectOption {
  value: SelectOptionValue;
  label: string;
  disabled?: boolean;
  description?: string;
  icon?: ReactNode;
}

export interface SelectProps {
  options: SelectOption[];
  value?: SelectValue;
  defaultValue?: SelectValue;
  placeholder?: string;
  label?: string;
  id?: string;
  disabled?: boolean;
  loading?: boolean;
  clearable?: boolean;
  searchable?: boolean;
  multiple?: boolean;
  size?: 'sm' | 'md' | 'lg';
  /**
   * Trigger appearance. `default` is the form-field look used across the app;
   * `toolbar` is the filter-bar control (40px, white, outline, 13px label).
   */
  variant?: 'default' | 'toolbar';
  /** Icon rendered before the display value (toolbar variant, e.g. Building2). */
  leadingIcon?: ReactNode;
  /** Accessible name for the trigger when no visible <label> is associated. */
  ariaLabel?: string;
  className?: string;
  dropdownClassName?: string;
  error?: string;
  required?: boolean;
  onChange?: (value: SelectValue, option?: SelectOption | SelectOption[]) => void;
  onSearch?: (searchTerm: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  renderOption?: (option: SelectOption, isSelected: boolean) => ReactNode;
  maxDropdownHeight?: number;
  position?: 'auto' | 'top' | 'bottom';
}
 
const Select: React.FC<SelectProps> = ({
  options = [],
  value,
  defaultValue = "",
  placeholder = 'Select an option...',
  label,
  id,
  disabled = false,
  loading = false,
  clearable = true,
  searchable = false,
  multiple = false,
  size = 'md',
  variant = 'default',
  leadingIcon,
  className = '',
  dropdownClassName = '',
  position="bottom",
  error,
  required = false,
  onChange,
  onSearch,
  onFocus,
  onBlur,
  renderOption,
  maxDropdownHeight = 300,
  ariaLabel,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [internalValue, setInternalValue] = useState(value ?? defaultValue ?? (multiple ? [] : ''));
  const selectRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Update internal value when prop changes
  useEffect(() => {
    if (value !== undefined) {
      setInternalValue(value);
    }
  }, [value]);
 
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
 
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
 
  // Filter options based on search term
  const filteredOptions = options?.filter(option =>
    String(option?.label || "").toLowerCase()?.includes(searchTerm?.toLowerCase())
  );
 
  // Get selected options for display
  const getSelectedOptions = (): SelectOption[] => {
    if (multiple) {
      const values = Array.isArray(internalValue) ? internalValue : [];
      return options?.filter(option => values.includes(option?.value));
    } else {
      return options?.filter(option => option?.value === internalValue);
    }
  };
 
  const selectedOptions = getSelectedOptions();
 
  // Handle option selection
  const handleOptionClick = (option: SelectOption) => {
    if (option.disabled) return;
 
    let newValue: SelectValue;
    let newSelectedOptions: SelectOption | SelectOption[];
 
    if (multiple) {
      const currentValues = Array.isArray(internalValue) ? internalValue : [];
      if (currentValues.includes(option.value)) {
        newValue = currentValues.filter(v => v !== option.value);
        newSelectedOptions = selectedOptions.filter(o => o.value !== option.value);
      } else {
        newValue = [...currentValues, option.value];
        newSelectedOptions = [...selectedOptions, option];
      }
    } else {
      newValue = option.value;
      newSelectedOptions = option;
      setIsOpen(false);
    }
 
    setInternalValue(newValue);
    onChange?.(newValue, newSelectedOptions);
    setSearchTerm('');
  };
 
  // Handle clear
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    const newValue = multiple ? [] : '';
    setInternalValue(newValue);
    onChange?.(newValue, multiple ? [] : undefined);
  };
 
  // Handle search
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value;
    setSearchTerm(term);
    onSearch?.(term);
  };
 
  const isToolbar = variant === 'toolbar';

  const iconSizes = {
    sm: 14,
    md: 16,
    lg: 18,
  };
 
  // Check if option is selected
  const isOptionSelected = (option: SelectOption): boolean => {
    if (multiple) {
      const values = Array.isArray(internalValue) ? internalValue : [];
      return values.includes(option.value);
    }
    return option.value === internalValue;
  };
 
  // Render display value
  const renderDisplayValue = () => {
    if (selectedOptions.length === 0) {
      return <span className="text-[var(--color-text-placeholder)]">{placeholder}</span>;
    }
 
    if (multiple) {
      if (selectedOptions.length === 1) {
        return selectedOptions[0].label;
      }
      return `${selectedOptions.length} selected`;
    }
 
    return selectedOptions[0]?.label;
  };
 
  return (
    <div ref={selectRef} className={`relative ${className}`}>
      {/* Label */}
      {label && (
        <label className="block text-xs font-semibold text-[var(--color-text-primary)] mb-[7px]">
          {label}
          {required && <span className="text-[var(--color-error)] ml-1">*</span>}
        </label>
      )}
      {/* Select Button */}
      <button
        type="button"
        id={id}
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        disabled={disabled || loading}
        onClick={() => {
          if (!disabled && !loading) {
            setIsOpen(!isOpen);
            if (!isOpen) onFocus?.();
          }
        }}
        onBlur={() => {
          setTimeout(() => {
            if (!selectRef.current?.contains(document.activeElement)) {
              onBlur?.();
            }
            
          }, 120);
        }}
        className={`
          w-full flex items-center justify-between rounded-[11px] border transition-all duration-200 outline-none
          ${isToolbar
            ? 'h-10 px-[13px] text-label font-semibold text-[var(--color-text-strong)]'
            : 'h-[44px] px-[14px] text-sm'}
          ${ isOpen
              ? 'border-[var(--color-border-focus)] shadow-[0_0_0_3px_rgba(109,40,217,0.14)]'
              : isToolbar
                ? 'border-[var(--color-border-control)] hover:border-[var(--color-border-input-hover)]'
                : 'border-[var(--color-border-primary)] hover:border-[var(--color-border-input-hover)]'
          }
          ${disabled || loading
            ? 'bg-[var(--color-bg-disabled)] text-[var(--color-text-disabled)] cursor-not-allowed'
            : error
              ? 'bg-white border-[var(--color-error)] shadow-[0_0_0_3px_var(--color-error-light)] text-[var(--color-text-primary)]'
              : isToolbar
                ? 'bg-[var(--color-bg-base)] text-[var(--color-text-strong)] cursor-pointer'
                : 'bg-[var(--color-bg-muted)] text-[var(--color-text-primary)] cursor-pointer'
          }
        `}
      >
        {isToolbar && leadingIcon && (
          <span className="mr-2 flex flex-none items-center text-[var(--color-text-tertiary)]">
            {leadingIcon}
          </span>
        )}
        <div className="flex-1 text-left truncate">
          {renderDisplayValue()}
        </div>
       
        <div className="flex items-center gap-1 ml-2">
          {clearable && selectedOptions.length > 0 && !disabled && !loading && internalValue !== defaultValue && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 hover:bg-[var(--color-bg-muted)] rounded transition-colors"
            >
              <X size={iconSizes[size]} className="text-[var(--color-text-tertiary)]" />
            </button>
          )}
         
          {loading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[var(--color-primary-600)]" />
          ) : (
            <ChevronDown
              size={isToolbar ? 15 : iconSizes[size]}
              className={`text-[var(--color-text-tertiary)] transition-transform duration-200 ${
                isOpen ? 'transform rotate-180' : ''
              }`}
            />
          )}
        </div>
      </button>
 
      {/* Dropdown */}
      {isOpen && !disabled && !loading && (
       <div
  ref={dropdownRef}
   className={`overflow-hidden
     absolute z-50 w-full bg-white border border-[var(--color-border-primary)] rounded-[13px] shadow-[var(--shadow-lg)]
     ${position === 'top' ? 'bottom-full mb-1' : 'mt-1'}
     ${dropdownClassName}
   `}
   style={{ maxHeight: maxDropdownHeight }}
>
          {/* Search Input */}
          {searchable && (
            <div className="p-2 border-b border-[var(--color-border-primary)]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--color-text-tertiary)]" size={14} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={handleSearch}
                  placeholder="Search options..."
                  className={`w-full ${isToolbar ? 'h-10 text-label font-normal' : 'h-[36px] text-sm'} pl-9 pr-3 bg-[var(--color-bg-muted)] border border-[var(--color-border-primary)] rounded-[9px] outline-none text-[var(--color-text-primary)] placeholder:text-[var(--color-text-placeholder)] focus:border-[var(--color-border-focus)] focus:shadow-[0_0_0_3px_rgba(109,40,217,0.14)] transition-all duration-200`}
                />
              </div>
            </div>
          )}
 
          {/* Options */}
          <div className={isToolbar ? "max-h-60 overflow-y-auto p-[6px]" : "max-h-60 overflow-y-auto"}>
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-[var(--color-text-tertiary)] text-center">
                {searchTerm ? 'No options found' : 'No options available'}
              </div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = isOptionSelected(option);
               
                return (
                  <button
                    key={option.value}
                    type="button"
                    disabled={option.disabled}
                    onClick={() => handleOptionClick(option)}
                    className={`
                      w-full ${isToolbar ? 'px-[14px] py-[10px] text-label rounded-[9px]' : 'px-3 py-2 text-sm'} text-left transition-colors duration-150
                      ${option.disabled
                        ? 'text-[var(--color-text-disabled)] cursor-not-allowed'
                        : isSelected
                          ? isToolbar
                            ? 'bg-[var(--color-primary-50)] text-[var(--color-primary-700)] font-bold'
                            : 'bg-[var(--color-primary-50)] text-[var(--color-primary-900)]'
                          : isToolbar
                            ? 'text-[var(--color-text-strong)] font-medium hover:bg-[var(--color-bg-muted)]'
                            : 'text-[var(--color-text-primary)] hover:bg-[var(--color-bg-muted)]'
                      }
                      flex items-center justify-between
                    `}
                  >
                    {renderOption ? (
                      renderOption(option, isSelected)
                    ) : (
                      <div className="flex items-center gap-2">
                        {option.icon && <span>{option.icon}</span>}
                        <div>
                          <div>{option.label}</div>
                          {option.description && (
                            <div className="text-xs text-[var(--color-text-tertiary)]">{option.description}</div>
                          )}
                        </div>
                      </div>
                    )}
                   
                    {isSelected && (
                      <Check size={16} className="text-[var(--color-primary-600)]" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
 
      {/* Error Message */}
      {error && (
        <div className="mt-1 text-xs text-[var(--color-error)]">
          {error}
        </div>
      )}
    </div>
  );
};
 
export default Select;